import { createRoot, Root } from 'react-dom/client';
import React from 'react';
import GlobalStyles from '@jbhi-fi/lanyard-ui/themes/GlobalStyles';
import GlobalTheme from '@jbhi-fi/lanyard-ui/themes/jb/JbGlobalTheme';
import { ServiceOffering } from '@jbhi-fi/apis/src/services/types';
import { CartContextProvider } from './context/CartContext';
import { ServiceOfferings } from './services/ServiceOfferings';
import { AttachedServices } from './services/AttachedServices';
import { ShopifyProductProvider } from '../../api/shopify/ShopifyProduct';
import { MinicartUiContextProvider } from './context/MinicartUiContext';
import { BundleOfferingsProvider } from '../../components/cart/BundleOfferings/BundleOfferingsContext';
import { BundleOfferings } from '../../components/cart/BundleOfferings/BundleOfferings';
import { QueryClient, QueryClientProvider } from 'react-query';
import { FrequentlyBoughtTogether } from './services/FrequentlyBoughtTogether';
import { ProductClientProvider } from '@jbhi-fi/components/src/context/ProductClientProvider';
import { ProductClient } from '../../api/algolia/ProductClient';
import { ShopifyCustomerProvider } from '../../contexts/ShopifyCustomerProvider';
import { PublicApiClientProvider } from '@jbhi-fi/components/src/context/PublicApiClientProvider';
import { KlaviyoOnSiteClient } from '@jbhi-fi/apis/src/klaviyo/KlaviyoOnSiteClient';
import { ShopifyProductActionsProvider } from '../../contexts/ShopifyProductActionsProvider';
import { CartAttach } from '../../handlebars/cart/CartAttach';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi/ContentfulThemeApiClient';
import { serviceHelper } from '../../config/service-helper';
import { CartItemAlerts } from './CartItemAlerts';
import { CartAlerts } from './CartAlerts';
import { CouponContextProvider } from '../../contexts/CouponContextProvider';
import { SubscriptionApiProvider } from '../../templates/components/customers/hooks/subscriptionApi';
import { SubscriptionTypes } from '../../templates/components/customers/api/subscriptions/types';
import { CustomerApiProvider } from '../../templates/components/customers/hooks/customerApi';
import { ShopifyCouponsWrapper } from '../../templates/components/cart/ShopifyCouponsWrapper';
import { CouponsModalWrapper } from '../../templates/components/cart/couponsModalWrapper';
import { KlaviyoBackInStoreClient } from '@jbhi-fi/apis/src/klaviyo/KlaviyoBackInStoreClient';
import { KlaviyoClient } from '@jbhi-fi/apis/src/klaviyo/KlaviyoClient';
import { CurrentBreakpointProvider } from '@jbhi-fi/components/src/context/CurrentBreakpointProvider';
import { ConfigProvider } from '@jbhi-fi/components/src/context/ConfigProvider';
import { AlternativesContextProvider } from '@jbhi-fi/components/src/ProductAlternatives/AlternativesContextProvider';

const klaviyoOnSiteClient = new KlaviyoOnSiteClient({
  klaviyoEnvironment: window.Jbhifi.klaviyoAccount,
  klaviyoSubscribeUrl: process.env.PUBLIC_KLAVIYO_NOTIFY_ME_URL || '',
  klaviyoNotifyMeListId: window.Jbhifi.klaviyoMailingListId,
});
const klaviyoClient = new KlaviyoClient({
  klaviyoEnvironment: window.Jbhifi.klaviyoAccount,
  klaviyoSubscribeUrl: process.env.PUBLIC_KLAVIYO_SUBSCRIBE_URL || '',
});
const klaviyoBackInStoreClient = new KlaviyoBackInStoreClient({
  klaviyoEnvironment: window.Jbhifi.klaviyoAccount,
  klaviyoBackInStoreUrl: process.env.PUBLIC_KLAVIYO_BACK_IN_STOCK_SUBSCRIPTIONS_URL || '',
});

const contentfulThemeApiClient = new ContentfulThemeApiClient({ themeApiBaseUrl: serviceHelper.themeApisUrl });

export const parseServiceOffering = (offering: { [key: string]: any }): ServiceOffering | null => {
  if (!offering) return null;

  return {
    ...offering,
    priceInc: parseInt(offering.priceInc),
  } as ServiceOffering;
};

let root: Root;

export const drawCartServices = ({ includeServiceOfferings = true } = {}) => {
  const container = window.document.getElementById('cart-services');
  const isCartPage = window.location.pathname === '/cart';
  const isCartAttachEnabled = window.Jbhifi?.attachConfig?.myCartEnabled;
  CartServicesReactReadyHelper.setIsReady(false);
  const isCartItemAlertsEnabled = window.featureFlags?.isCartItemAlertsEnabled === true;
  const miniCartFbtModel = window.featureFlags?.miniCartFbtModel ?? 'FBT';
  const cartAlertContainerClassName = isCartPage ? 'cart-page__alerts' : 'cart-mini__alerts';
  const settings = window.smartModuleSettings?.cartPage;
  const myCartFbtModel =
    window.featureFlags?.myCartFbtModel && settings.isEnabled ? settings?.googleRecModelType : 'FBT';

  if (container) {
    if (!root) {
      root = createRoot(container);
    }

    root.render(
      <>
        <GlobalStyles />
        <GlobalTheme />
        <ConfigProvider contentfulImageQuality={window.Jbhifi.contentfulSettings?.defaultContentfulImageQuality}>
          <CurrentBreakpointProvider>
            <ShopifyProductProvider>
              <MinicartUiContextProvider>
                <CartContextProvider>
                  <QueryClientProvider client={new QueryClient()}>
                    <PublicApiClientProvider
                      klaviyoClient={klaviyoClient}
                      klaviyoOnSiteClient={klaviyoOnSiteClient}
                      klaviyoBackInStoreClient={klaviyoBackInStoreClient}
                      contentfulThemeApiClient={contentfulThemeApiClient}
                    >
                      <ProductClientProvider client={new ProductClient()}>
                        <ShopifyCustomerProvider>
                          <ShopifyProductActionsProvider>
                            <AlternativesContextProvider>
                              <AttachedServices />
                              {isCartItemAlertsEnabled ? <CartItemAlerts isCartPage={isCartPage} /> : null}
                              <CartAlerts
                                cartAlertContainerClassName={cartAlertContainerClassName}
                                isCartPage={isCartPage}
                              />
                              {includeServiceOfferings && (!isCartPage || !isCartAttachEnabled) ? (
                                <ServiceOfferings />
                              ) : null}
                              {!isCartPage || !isCartAttachEnabled ? (
                                <BundleOfferingsProvider>
                                  <BundleOfferings />
                                </BundleOfferingsProvider>
                              ) : null}
                              {isCartAttachEnabled ? (
                                <CartAttach />
                              ) : settings?.isEnabled ? (
                                <FrequentlyBoughtTogether
                                  googleRecIndex={isCartPage ? myCartFbtModel : miniCartFbtModel}
                                />
                              ) : null}
                              {isCartPage ? (
                                <CustomerApiProvider
                                  linkedId={{
                                    sourceSystem: 'Shopify',
                                    sourceSystemId: window.Jbhifi?.shopifyCustomerId,
                                  }}
                                >
                                  <SubscriptionApiProvider subscriptionTypes={[SubscriptionTypes.perks]}>
                                    <CouponContextProvider>
                                      <ShopifyCouponsWrapper containerIdName="coupons_available" />
                                      <CouponsModalWrapper />
                                    </CouponContextProvider>
                                  </SubscriptionApiProvider>
                                </CustomerApiProvider>
                              ) : null}
                            </AlternativesContextProvider>
                          </ShopifyProductActionsProvider>
                        </ShopifyCustomerProvider>
                      </ProductClientProvider>
                    </PublicApiClientProvider>
                  </QueryClientProvider>
                </CartContextProvider>
              </MinicartUiContextProvider>
            </ShopifyProductProvider>
          </CurrentBreakpointProvider>
        </ConfigProvider>
      </>
    );
  }
};

export class CartServicesReactReadyHelper {
  private static LOCAL_STORAGE_KEY = 'cartServicesReactReady';
  static setIsReady = (readyStatus: boolean): void => {
    localStorage.setItem(CartServicesReactReadyHelper.LOCAL_STORAGE_KEY, readyStatus ? '1' : '0');
  };

  static getIsReady = (): boolean => {
    return localStorage.getItem(CartServicesReactReadyHelper.LOCAL_STORAGE_KEY) === '1';
  };
}
